import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { graphApiCall } from '../../utils/graphClient';
import { authenticatedApiCall } from '../../utils/apiClient';

// ─── Priority / progress maps ────────────────────────────────────────────────
const PRIORITY_MAP = { 0: 'Urgent', 1: 'Important', 5: 'Medium', 9: 'Low' };
const PRIORITY_COLORS = { 0: '#d32f2f', 1: '#f57c00', 5: '#1976d2', 9: '#757575' };
const PROGRESS_OPTIONS = ['notStarted', 'inProgress', 'completed'];
const PROGRESS_LABELS = { notStarted: 'Not Started', inProgress: 'In Progress', completed: 'Completed' };
const PROGRESS_COLORS = { notStarted: '#9e9e9e', inProgress: '#1976d2', completed: '#2e7d32' };

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const isOverdue = (dueDate, pct) => {
  if (!dueDate || pct === 100) return false;
  return new Date(dueDate) < new Date();
};

// ─── Main component ─────────────────────────────────────────────────────────
const Planner = ({ jobNumber }) => {
  const { instance, accounts } = useMsal();

  // Plan link state
  const [planLink, setPlanLink] = useState(null);
  const [loadingLink, setLoadingLink] = useState(true);

  // Plan picker
  const [showPlanPicker, setShowPlanPicker] = useState(false);
  const [groups, setGroups] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [planSearchTerm, setPlanSearchTerm] = useState('');

  // Board state
  const [buckets, setBuckets] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState({});
  const [loadingBoard, setLoadingBoard] = useState(false);
  const [boardError, setBoardError] = useState(null);

  // Task detail
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDetails, setTaskDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // New task
  const [showNewTask, setShowNewTask] = useState(false);
  const [newTaskBucketId, setNewTaskBucketId] = useState(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [creatingTask, setCreatingTask] = useState(false);

  // View toggle
  const [viewMode, setViewMode] = useState('board'); // board | list

  // ─── Fetch plan link on mount ──────────────────────────────────────────────
  useEffect(() => {
    if (!jobNumber) return;
    (async () => {
      try {
        const res = await authenticatedApiCall(instance, accounts, 'get', `/planner/${jobNumber}/link`);
        setPlanLink(res.data?.link || null);
      } catch (e) {
        console.error('Error fetching planner link:', e);
      } finally {
        setLoadingLink(false);
      }
    })();
  }, [jobNumber, instance, accounts]);

  // ─── Fetch board data when plan is linked ──────────────────────────────────
  const fetchBoard = useCallback(async () => {
    if (!planLink?.plan_id) return;
    setLoadingBoard(true);
    setBoardError(null);
    try {
      const [bucketsRes, tasksRes] = await Promise.all([
        graphApiCall(instance, accounts, `/planner/plans/${planLink.plan_id}/buckets`),
        graphApiCall(instance, accounts, `/planner/plans/${planLink.plan_id}/tasks`),
      ]);
      setBuckets((bucketsRes?.value || []).sort((a, b) => (a.orderHint || '').localeCompare(b.orderHint || '')));
      setTasks(tasksRes?.value || []);

      // Resolve unique user IDs
      const userIds = new Set();
      (tasksRes?.value || []).forEach((t) => {
        if (t.assignments) Object.keys(t.assignments).forEach((uid) => userIds.add(uid));
      });
      const newMembers = { ...members };
      await Promise.all(
        [...userIds].filter((uid) => !newMembers[uid]).map(async (uid) => {
          try {
            const u = await graphApiCall(instance, accounts, `/users/${uid}`);
            newMembers[uid] = { displayName: u.displayName, mail: u.mail };
          } catch {
            newMembers[uid] = { displayName: uid.slice(0, 8), mail: '' };
          }
        })
      );
      setMembers(newMembers);
    } catch (e) {
      console.error('Error loading board:', e);
      setBoardError(e.message);
    } finally {
      setLoadingBoard(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planLink?.plan_id, instance, accounts]);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  // ─── Plan picker ───────────────────────────────────────────────────────────
  const openPlanPicker = async () => {
    setShowPlanPicker(true);
    setLoadingGroups(true);
    try {
      const res = await graphApiCall(instance, accounts, '/me/memberOf/microsoft.graph.group?$filter=groupTypes/any(c:c eq \'Unified\')&$select=id,displayName&$top=200');
      setGroups((res?.value || []).sort((a, b) => a.displayName.localeCompare(b.displayName)));
    } catch (e) {
      console.error('Error loading groups:', e);
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  const selectGroup = async (group) => {
    setSelectedGroupId(group.id);
    setLoadingPlans(true);
    try {
      const res = await graphApiCall(instance, accounts, `/groups/${group.id}/planner/plans`);
      setPlans((res?.value || []).sort((a, b) => a.title.localeCompare(b.title)));
    } catch (e) {
      console.error('Error loading plans:', e);
      setPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  const linkPlan = async (plan, group) => {
    try {
      const groupObj = groups.find((g) => g.id === group);
      await authenticatedApiCall(instance, accounts, 'put', `/planner/${jobNumber}/link`, {
        planId: plan.id,
        planTitle: plan.title,
        groupId: group,
        groupName: groupObj?.displayName || '',
      });
      setPlanLink({ plan_id: plan.id, plan_title: plan.title, group_id: group, group_name: groupObj?.displayName });
      setShowPlanPicker(false);
      setSelectedGroupId(null);
      setPlans([]);
    } catch (e) {
      console.error('Error linking plan:', e);
    }
  };

  const unlinkPlan = async () => {
    if (!window.confirm('Unlink this Planner plan from the project?')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'delete', `/planner/${jobNumber}/link`);
      setPlanLink(null);
      setBuckets([]);
      setTasks([]);
    } catch (e) {
      console.error('Error unlinking plan:', e);
    }
  };

  // ─── Task detail ───────────────────────────────────────────────────────────
  const openTaskDetail = async (task) => {
    setSelectedTask(task);
    setLoadingDetails(true);
    try {
      const details = await graphApiCall(instance, accounts, `/planner/tasks/${task.id}/details`);
      setTaskDetails(details);
    } catch (e) {
      console.error('Error loading task details:', e);
      setTaskDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeTaskDetail = () => {
    setSelectedTask(null);
    setTaskDetails(null);
  };

  // ─── Update task progress ─────────────────────────────────────────────────
  const updateTaskProgress = async (taskId, percentComplete, etag) => {
    try {
      await graphApiCall(instance, accounts, `/planner/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'If-Match': etag },
        body: JSON.stringify({ percentComplete }),
      });
      await fetchBoard();
    } catch (e) {
      console.error('Error updating task:', e);
      alert('Failed to update task. Someone else may have modified it.');
    }
  };

  // ─── Create task ───────────────────────────────────────────────────────────
  const createTask = async () => {
    if (!newTaskTitle.trim()) return;
    setCreatingTask(true);
    try {
      await graphApiCall(instance, accounts, '/planner/tasks', {
        method: 'POST',
        body: JSON.stringify({
          planId: planLink.plan_id,
          bucketId: newTaskBucketId,
          title: newTaskTitle.trim(),
        }),
      });
      setNewTaskTitle('');
      setShowNewTask(false);
      setNewTaskBucketId(null);
      await fetchBoard();
    } catch (e) {
      console.error('Error creating task:', e);
      alert('Failed to create task: ' + e.message);
    } finally {
      setCreatingTask(false);
    }
  };

  // ─── Grouped tasks for board ───────────────────────────────────────────────
  const tasksByBucket = useMemo(() => {
    const map = {};
    buckets.forEach((b) => { map[b.id] = []; });
    tasks.forEach((t) => {
      if (map[t.bucketId]) map[t.bucketId].push(t);
      else map[t.bucketId] = [t];
    });
    return map;
  }, [buckets, tasks]);

  // Search / filter for list view
  const [listSearch, setListSearch] = useState('');
  const [listFilter, setListFilter] = useState('all');
  const filteredTasks = useMemo(() => {
    let list = [...tasks];
    if (listSearch) {
      const q = listSearch.toLowerCase();
      list = list.filter((t) => t.title.toLowerCase().includes(q));
    }
    if (listFilter === 'overdue') list = list.filter((t) => isOverdue(t.dueDateTime, t.percentComplete));
    else if (listFilter === 'completed') list = list.filter((t) => t.percentComplete === 100);
    else if (listFilter === 'inProgress') list = list.filter((t) => t.percentComplete > 0 && t.percentComplete < 100);
    else if (listFilter === 'notStarted') list = list.filter((t) => t.percentComplete === 0);
    return list;
  }, [tasks, listSearch, listFilter]);

  // Stats
  const stats = useMemo(() => {
    const total = tasks.length;
    const completed = tasks.filter((t) => t.percentComplete === 100).length;
    const inProg = tasks.filter((t) => t.percentComplete > 0 && t.percentComplete < 100).length;
    const overdue = tasks.filter((t) => isOverdue(t.dueDateTime, t.percentComplete)).length;
    return { total, completed, inProg, overdue, pct: total ? Math.round((completed / total) * 100) : 0 };
  }, [tasks]);

  // ─── New task input ref for auto-focus ─────────────────────────────────────
  const newTaskInputRef = useRef(null);
  useEffect(() => {
    if (showNewTask && newTaskInputRef.current) newTaskInputRef.current.focus();
  }, [showNewTask]);

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  if (loadingLink) {
    return (
      <div style={styles.center}>
        <div style={styles.spinner} />
        <p style={{ color: '#666', marginTop: 12 }}>Loading Planner...</p>
      </div>
    );
  }

  // ─── Plan picker modal ─────────────────────────────────────────────────────
  if (showPlanPicker) {
    const filteredGroups = planSearchTerm
      ? groups.filter((g) => g.displayName.toLowerCase().includes(planSearchTerm.toLowerCase()))
      : groups;

    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>📅 Link a Planner Plan</h2>
          <button style={styles.btnSecondary} onClick={() => { setShowPlanPicker(false); setSelectedGroupId(null); setPlans([]); setPlanSearchTerm(''); }}>
            ← Back
          </button>
        </div>

        <p style={styles.subtitle}>
          Select a Microsoft 365 Group, then choose a Planner plan to link to this project.
        </p>

        <input
          type="text"
          placeholder="Search groups..."
          value={planSearchTerm}
          onChange={(e) => setPlanSearchTerm(e.target.value)}
          style={styles.searchInput}
        />

        <div style={styles.pickerGrid}>
          {/* Groups column */}
          <div style={styles.pickerColumn}>
            <h3 style={styles.pickerColumnTitle}>Groups</h3>
            {loadingGroups ? (
              <div style={styles.center}><div style={styles.spinnerSmall} /></div>
            ) : filteredGroups.length === 0 ? (
              <p style={styles.emptyText}>No groups found. Make sure your Azure admin has granted Group.Read.All permission.</p>
            ) : (
              <div style={styles.pickerList}>
                {filteredGroups.map((g) => (
                  <div
                    key={g.id}
                    style={{ ...styles.pickerItem, ...(selectedGroupId === g.id ? styles.pickerItemActive : {}) }}
                    onClick={() => selectGroup(g)}
                  >
                    <span style={styles.groupIcon}>👥</span>
                    <span>{g.displayName}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Plans column */}
          <div style={styles.pickerColumn}>
            <h3 style={styles.pickerColumnTitle}>Plans</h3>
            {!selectedGroupId ? (
              <p style={styles.emptyText}>Select a group to see its plans</p>
            ) : loadingPlans ? (
              <div style={styles.center}><div style={styles.spinnerSmall} /></div>
            ) : plans.length === 0 ? (
              <p style={styles.emptyText}>No plans in this group</p>
            ) : (
              <div style={styles.pickerList}>
                {plans.map((p) => (
                  <div key={p.id} style={styles.pickerItem} onClick={() => linkPlan(p, selectedGroupId)}>
                    <span style={styles.planIcon}>📋</span>
                    <span>{p.title}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── No plan linked yet ────────────────────────────────────────────────────
  if (!planLink) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📋</div>
          <h2 style={styles.emptyTitle}>Microsoft Planner</h2>
          <p style={styles.emptyDesc}>
            Connect a Microsoft Planner plan to this project to manage tasks, track progress,
            and collaborate with your team directly from the portal.
          </p>
          <button style={styles.btnPrimary} onClick={openPlanPicker}>
            Link a Planner Plan
          </button>
          <p style={styles.prereqNote}>
            Requires Azure admin to grant Tasks.ReadWrite, Group.Read.All, and User.Read
            permissions in the App Registration.
          </p>
        </div>
      </div>
    );
  }

  // ─── Board / List view ─────────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h2 style={styles.title}>📋 {planLink.plan_title}</h2>
          {planLink.group_name && <span style={styles.groupBadge}>{planLink.group_name}</span>}
        </div>
        <div style={styles.headerRight}>
          <div style={styles.viewToggle}>
            <button
              style={viewMode === 'board' ? styles.viewBtnActive : styles.viewBtn}
              onClick={() => setViewMode('board')}
            >
              Board
            </button>
            <button
              style={viewMode === 'list' ? styles.viewBtnActive : styles.viewBtn}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
          </div>
          <button style={styles.btnIcon} title="Refresh" onClick={fetchBoard}>🔄</button>
          <button style={styles.btnDanger} title="Unlink plan" onClick={unlinkPlan}>Unlink</button>
        </div>
      </div>

      {/* Stats bar */}
      <div style={styles.statsBar}>
        <div style={styles.stat}><span style={styles.statNum}>{stats.total}</span> Total</div>
        <div style={styles.stat}><span style={{ ...styles.statNum, color: PROGRESS_COLORS.completed }}>{stats.completed}</span> Done</div>
        <div style={styles.stat}><span style={{ ...styles.statNum, color: PROGRESS_COLORS.inProgress }}>{stats.inProg}</span> In Progress</div>
        {stats.overdue > 0 && (
          <div style={styles.stat}><span style={{ ...styles.statNum, color: '#d32f2f' }}>{stats.overdue}</span> Overdue</div>
        )}
        <div style={styles.progressBarOuter}>
          <div style={{ ...styles.progressBarInner, width: `${stats.pct}%` }} />
          <span style={styles.progressBarText}>{stats.pct}%</span>
        </div>
      </div>

      {/* Error */}
      {boardError && (
        <div style={styles.errorBanner}>
          {boardError}
          <button style={styles.btnSmall} onClick={fetchBoard}>Retry</button>
        </div>
      )}

      {/* Loading */}
      {loadingBoard ? (
        <div style={styles.center}><div style={styles.spinner} /><p style={{ color: '#666', marginTop: 12 }}>Loading tasks...</p></div>
      ) : viewMode === 'board' ? (
        /* ── BOARD VIEW ─────────────────────────────────────────────────────── */
        <div style={styles.boardScroll}>
          <div style={styles.board}>
            {buckets.map((bucket) => (
              <div key={bucket.id} style={styles.column}>
                <div style={styles.columnHeader}>
                  <h3 style={styles.columnTitle}>{bucket.name}</h3>
                  <span style={styles.columnCount}>{(tasksByBucket[bucket.id] || []).length}</span>
                </div>
                <div style={styles.columnBody}>
                  {(tasksByBucket[bucket.id] || []).map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      members={members}
                      onClick={() => openTaskDetail(task)}
                    />
                  ))}
                  {/* Add task button */}
                  {showNewTask && newTaskBucketId === bucket.id ? (
                    <div style={styles.newTaskForm}>
                      <input
                        ref={newTaskInputRef}
                        style={styles.newTaskInput}
                        placeholder="Task title..."
                        value={newTaskTitle}
                        onChange={(e) => setNewTaskTitle(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') createTask(); if (e.key === 'Escape') { setShowNewTask(false); setNewTaskTitle(''); } }}
                        disabled={creatingTask}
                      />
                      <div style={styles.newTaskActions}>
                        <button style={styles.btnPrimarySmall} onClick={createTask} disabled={creatingTask || !newTaskTitle.trim()}>
                          {creatingTask ? '...' : 'Add'}
                        </button>
                        <button style={styles.btnSecondarySmall} onClick={() => { setShowNewTask(false); setNewTaskTitle(''); }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button
                      style={styles.addTaskBtn}
                      onClick={() => { setShowNewTask(true); setNewTaskBucketId(bucket.id); setNewTaskTitle(''); }}
                    >
                      + Add task
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        /* ── LIST VIEW ──────────────────────────────────────────────────────── */
        <div style={styles.listView}>
          <div style={styles.listToolbar}>
            <input
              style={styles.searchInput}
              placeholder="Search tasks..."
              value={listSearch}
              onChange={(e) => setListSearch(e.target.value)}
            />
            <select style={styles.filterSelect} value={listFilter} onChange={(e) => setListFilter(e.target.value)}>
              <option value="all">All Tasks</option>
              <option value="notStarted">Not Started</option>
              <option value="inProgress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Task</th>
                <th style={{ ...styles.th, width: 120 }}>Bucket</th>
                <th style={{ ...styles.th, width: 100 }}>Priority</th>
                <th style={{ ...styles.th, width: 100 }}>Progress</th>
                <th style={{ ...styles.th, width: 110 }}>Due Date</th>
                <th style={{ ...styles.th, width: 140 }}>Assigned To</th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.length === 0 ? (
                <tr><td colSpan={6} style={styles.emptyCell}>No tasks match the filter</td></tr>
              ) : (
                filteredTasks.map((task) => {
                  const bucket = buckets.find((b) => b.id === task.bucketId);
                  const overdue = isOverdue(task.dueDateTime, task.percentComplete);
                  const assignees = task.assignments ? Object.keys(task.assignments).map((uid) => members[uid]?.displayName || uid.slice(0, 8)) : [];
                  const priorityKey = task.priority ?? 5;
                  return (
                    <tr key={task.id} style={styles.tr} onClick={() => openTaskDetail(task)}>
                      <td style={styles.td}>
                        <span style={task.percentComplete === 100 ? styles.taskTitleDone : undefined}>{task.title}</span>
                      </td>
                      <td style={styles.td}>{bucket?.name || '—'}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.priorityBadge, background: PRIORITY_COLORS[priorityKey] || '#757575' }}>
                          {PRIORITY_MAP[priorityKey] || 'Medium'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        <span style={{ ...styles.progressBadge, background: task.percentComplete === 100 ? PROGRESS_COLORS.completed : task.percentComplete > 0 ? PROGRESS_COLORS.inProgress : PROGRESS_COLORS.notStarted }}>
                          {task.percentComplete === 100 ? 'Done' : task.percentComplete > 0 ? 'In Progress' : 'Not Started'}
                        </span>
                      </td>
                      <td style={{ ...styles.td, color: overdue ? '#d32f2f' : undefined, fontWeight: overdue ? 600 : undefined }}>
                        {fmtDate(task.dueDateTime)}
                      </td>
                      <td style={styles.td}>{assignees.join(', ') || '—'}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Task detail drawer ────────────────────────────────────────────── */}
      {selectedTask && (
        <div style={styles.drawerOverlay} onClick={closeTaskDetail}>
          <div style={styles.drawer} onClick={(e) => e.stopPropagation()}>
            <div style={styles.drawerHeader}>
              <h3 style={styles.drawerTitle}>{selectedTask.title}</h3>
              <button style={styles.drawerClose} onClick={closeTaskDetail}>✕</button>
            </div>

            {loadingDetails ? (
              <div style={styles.center}><div style={styles.spinnerSmall} /></div>
            ) : (
              <div style={styles.drawerBody}>
                {/* Progress */}
                <div style={styles.detailSection}>
                  <label style={styles.detailLabel}>Progress</label>
                  <div style={styles.progressButtons}>
                    {PROGRESS_OPTIONS.map((opt) => {
                      const pctVal = opt === 'notStarted' ? 0 : opt === 'inProgress' ? 50 : 100;
                      const isActive = (selectedTask.percentComplete === 0 && opt === 'notStarted') ||
                        (selectedTask.percentComplete > 0 && selectedTask.percentComplete < 100 && opt === 'inProgress') ||
                        (selectedTask.percentComplete === 100 && opt === 'completed');
                      return (
                        <button
                          key={opt}
                          style={{ ...styles.progressBtn, ...(isActive ? { background: PROGRESS_COLORS[opt], color: '#fff' } : {}) }}
                          onClick={() => updateTaskProgress(selectedTask.id, pctVal, selectedTask['@odata.etag'])}
                        >
                          {PROGRESS_LABELS[opt]}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Priority */}
                <div style={styles.detailSection}>
                  <label style={styles.detailLabel}>Priority</label>
                  <span style={{ ...styles.priorityBadge, background: PRIORITY_COLORS[selectedTask.priority ?? 5] || '#757575' }}>
                    {PRIORITY_MAP[selectedTask.priority ?? 5] || 'Medium'}
                  </span>
                </div>

                {/* Bucket */}
                <div style={styles.detailSection}>
                  <label style={styles.detailLabel}>Bucket</label>
                  <span>{buckets.find((b) => b.id === selectedTask.bucketId)?.name || '—'}</span>
                </div>

                {/* Due date */}
                <div style={styles.detailSection}>
                  <label style={styles.detailLabel}>Due Date</label>
                  <span style={isOverdue(selectedTask.dueDateTime, selectedTask.percentComplete) ? { color: '#d32f2f', fontWeight: 600 } : undefined}>
                    {fmtDate(selectedTask.dueDateTime) || 'No due date'}
                  </span>
                </div>

                {/* Start date */}
                <div style={styles.detailSection}>
                  <label style={styles.detailLabel}>Start Date</label>
                  <span>{fmtDate(selectedTask.startDateTime) || 'No start date'}</span>
                </div>

                {/* Assigned */}
                <div style={styles.detailSection}>
                  <label style={styles.detailLabel}>Assigned To</label>
                  <div style={styles.assigneeList}>
                    {selectedTask.assignments && Object.keys(selectedTask.assignments).length > 0
                      ? Object.keys(selectedTask.assignments).map((uid) => (
                        <span key={uid} style={styles.assigneeBadge}>
                          {members[uid]?.displayName || uid.slice(0, 8)}
                        </span>
                      ))
                      : <span style={{ color: '#999' }}>Unassigned</span>
                    }
                  </div>
                </div>

                {/* Description / checklist */}
                {taskDetails?.description && (
                  <div style={styles.detailSection}>
                    <label style={styles.detailLabel}>Notes</label>
                    <div style={styles.descriptionBox}>{taskDetails.description}</div>
                  </div>
                )}

                {/* Checklist */}
                {taskDetails?.checklist && Object.keys(taskDetails.checklist).length > 0 && (
                  <div style={styles.detailSection}>
                    <label style={styles.detailLabel}>Checklist</label>
                    <div style={styles.checklist}>
                      {Object.entries(taskDetails.checklist)
                        .sort(([, a], [, b]) => (a.orderHint || '').localeCompare(b.orderHint || ''))
                        .map(([id, item]) => (
                          <div key={id} style={styles.checkItem}>
                            <span style={item.isChecked ? styles.checkDone : styles.checkUndone}>
                              {item.isChecked ? '☑' : '☐'}
                            </span>
                            <span style={item.isChecked ? styles.taskTitleDone : undefined}>{item.title}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Dates */}
                <div style={styles.detailSection}>
                  <label style={styles.detailLabel}>Created</label>
                  <span style={{ color: '#999', fontSize: 13 }}>{fmtDate(selectedTask.createdDateTime)}</span>
                </div>

                {/* Open in Planner link */}
                <div style={{ ...styles.detailSection, borderTop: '1px solid #e0e0e0', paddingTop: 16, marginTop: 8 }}>
                  <a
                    href={`https://tasks.office.com/home/task/${selectedTask.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.openInPlanner}
                  >
                    Open in Microsoft Planner ↗
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Task card sub-component ─────────────────────────────────────────────────
const TaskCard = ({ task, members, onClick }) => {
  const overdue = isOverdue(task.dueDateTime, task.percentComplete);
  const priorityKey = task.priority ?? 5;
  const assignees = task.assignments ? Object.keys(task.assignments).map((uid) => members[uid]?.displayName || uid.slice(0, 8)) : [];

  return (
    <div style={{ ...styles.card, ...(overdue ? styles.cardOverdue : {}) }} onClick={onClick}>
      {/* Priority strip */}
      <div style={{ ...styles.cardPriorityStrip, background: PRIORITY_COLORS[priorityKey] || '#757575' }} />

      <div style={styles.cardContent}>
        <p style={task.percentComplete === 100 ? { ...styles.cardTitle, ...styles.taskTitleDone } : styles.cardTitle}>
          {task.title}
        </p>

        <div style={styles.cardMeta}>
          {task.dueDateTime && (
            <span style={{ ...styles.cardDue, color: overdue ? '#d32f2f' : '#666' }}>
              📅 {fmtDate(task.dueDateTime)}
            </span>
          )}
          {task.percentComplete === 100 && <span style={styles.cardDone}>✓ Done</span>}
          {task.percentComplete > 0 && task.percentComplete < 100 && (
            <span style={styles.cardInProgress}>{task.percentComplete}%</span>
          )}
        </div>

        {assignees.length > 0 && (
          <div style={styles.cardAssignees}>
            {assignees.map((name, i) => (
              <span key={i} style={styles.avatar} title={name}>{name.charAt(0).toUpperCase()}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════════════════════
const styles = {
  container: { padding: '20px 24px', height: '100%', display: 'flex', flexDirection: 'column', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40 },

  // Header
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  headerRight: { display: 'flex', alignItems: 'center', gap: 8 },
  title: { margin: 0, fontSize: 20, fontWeight: 600, color: '#1a1a1a' },
  subtitle: { color: '#666', marginBottom: 16, fontSize: 14 },
  groupBadge: { background: '#e3f2fd', color: '#1565c0', padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 500 },

  // View toggle
  viewToggle: { display: 'flex', border: '1px solid #ddd', borderRadius: 6, overflow: 'hidden' },
  viewBtn: { padding: '6px 14px', border: 'none', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#666' },
  viewBtnActive: { padding: '6px 14px', border: 'none', background: '#1976d2', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 },

  // Buttons
  btnPrimary: { padding: '10px 24px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 500 },
  btnSecondary: { padding: '8px 16px', background: '#f5f5f5', color: '#333', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  btnDanger: { padding: '6px 14px', background: '#fff', color: '#d32f2f', border: '1px solid #ef9a9a', borderRadius: 6, cursor: 'pointer', fontSize: 12 },
  btnIcon: { padding: '6px 10px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: 16 },
  btnSmall: { padding: '4px 10px', background: '#fff', border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginLeft: 8 },
  btnPrimarySmall: { padding: '6px 14px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 },
  btnSecondarySmall: { padding: '6px 14px', background: '#f5f5f5', color: '#333', border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer', fontSize: 13 },

  // Stats bar
  statsBar: { display: 'flex', alignItems: 'center', gap: 20, padding: '10px 16px', background: '#f8f9fa', borderRadius: 8, marginBottom: 16, flexWrap: 'wrap' },
  stat: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#555' },
  statNum: { fontWeight: 700, fontSize: 16 },
  progressBarOuter: { flex: 1, minWidth: 120, height: 20, background: '#e0e0e0', borderRadius: 10, position: 'relative', overflow: 'hidden' },
  progressBarInner: { height: '100%', background: '#2e7d32', borderRadius: 10, transition: 'width 0.4s ease' },
  progressBarText: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#333' },

  // Error
  errorBanner: { padding: '10px 16px', background: '#ffebee', color: '#c62828', borderRadius: 6, marginBottom: 12, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },

  // Board
  boardScroll: { flex: 1, overflowX: 'auto', overflowY: 'hidden' },
  board: { display: 'flex', gap: 16, minHeight: 400, paddingBottom: 16 },
  column: { flex: '0 0 300px', background: '#f4f5f7', borderRadius: 8, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 280px)' },
  columnHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px 8px', borderBottom: '2px solid #e0e0e0' },
  columnTitle: { margin: 0, fontSize: 14, fontWeight: 600, color: '#333' },
  columnCount: { background: '#e0e0e0', color: '#666', padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 600 },
  columnBody: { flex: 1, overflowY: 'auto', padding: '8px 10px' },

  // Card
  card: { background: '#fff', borderRadius: 6, marginBottom: 8, cursor: 'pointer', display: 'flex', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', transition: 'box-shadow 0.15s', border: '1px solid #e8e8e8' },
  cardOverdue: { borderLeft: '3px solid #d32f2f' },
  cardPriorityStrip: { width: 4, borderRadius: '6px 0 0 6px', flexShrink: 0 },
  cardContent: { padding: '10px 12px', flex: 1, minWidth: 0 },
  cardTitle: { margin: '0 0 6px', fontSize: 13, fontWeight: 500, color: '#1a1a1a', lineHeight: 1.4 },
  cardMeta: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  cardDue: { fontSize: 11, display: 'flex', alignItems: 'center', gap: 3 },
  cardDone: { fontSize: 11, color: '#2e7d32', fontWeight: 600 },
  cardInProgress: { fontSize: 11, color: '#1976d2', fontWeight: 600 },
  cardAssignees: { display: 'flex', gap: 4, marginTop: 8 },
  avatar: { width: 24, height: 24, borderRadius: '50%', background: '#1976d2', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600 },

  // Add task
  addTaskBtn: { width: '100%', padding: '8px 12px', background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', fontSize: 13, textAlign: 'left', borderRadius: 4 },
  newTaskForm: { padding: 8, background: '#fff', borderRadius: 6, border: '1px solid #ddd', marginBottom: 8 },
  newTaskInput: { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 4, fontSize: 13, boxSizing: 'border-box', marginBottom: 6 },
  newTaskActions: { display: 'flex', gap: 6 },

  // List view
  listView: { flex: 1, overflow: 'auto' },
  listToolbar: { display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' },
  searchInput: { padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, flex: 1, minWidth: 200 },
  filterSelect: { padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, background: '#fff' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e0e0e0', fontSize: 12, fontWeight: 600, color: '#555', textTransform: 'uppercase', background: '#fafafa' },
  tr: { cursor: 'pointer', transition: 'background 0.1s' },
  td: { padding: '10px 12px', borderBottom: '1px solid #f0f0f0', fontSize: 13 },
  emptyCell: { textAlign: 'center', color: '#999', padding: 32, fontSize: 14 },

  // Badges
  priorityBadge: { color: '#fff', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 },
  progressBadge: { color: '#fff', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 },

  // Task title done
  taskTitleDone: { textDecoration: 'line-through', color: '#999' },

  // Drawer
  drawerOverlay: { position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, background: 'rgba(0,0,0,0.3)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' },
  drawer: { width: 440, maxWidth: '100vw', background: '#fff', height: '100%', boxShadow: '-4px 0 20px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  drawerHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '20px 24px 16px', borderBottom: '1px solid #e0e0e0' },
  drawerTitle: { margin: 0, fontSize: 18, fontWeight: 600, color: '#1a1a1a', flex: 1, paddingRight: 12 },
  drawerClose: { background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#666', padding: '0 4px' },
  drawerBody: { flex: 1, overflow: 'auto', padding: '16px 24px 24px' },

  // Detail sections
  detailSection: { marginBottom: 16 },
  detailLabel: { display: 'block', fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', marginBottom: 6, letterSpacing: 0.5 },
  progressButtons: { display: 'flex', gap: 6 },
  progressBtn: { padding: '6px 14px', border: '1px solid #ddd', borderRadius: 16, background: '#f5f5f5', cursor: 'pointer', fontSize: 12, fontWeight: 500 },
  assigneeList: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  assigneeBadge: { background: '#e3f2fd', color: '#1565c0', padding: '4px 10px', borderRadius: 12, fontSize: 12 },
  descriptionBox: { background: '#f9f9f9', padding: 12, borderRadius: 6, fontSize: 13, color: '#333', lineHeight: 1.6, whiteSpace: 'pre-wrap' },
  checklist: { display: 'flex', flexDirection: 'column', gap: 6 },
  checkItem: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 },
  checkDone: { color: '#2e7d32' },
  checkUndone: { color: '#bbb' },
  openInPlanner: { color: '#1976d2', textDecoration: 'none', fontWeight: 500, fontSize: 14 },

  // Empty state
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, padding: '60px 20px', textAlign: 'center' },
  emptyIcon: { fontSize: 56, marginBottom: 16 },
  emptyTitle: { margin: '0 0 8px', fontSize: 22, fontWeight: 600, color: '#1a1a1a' },
  emptyDesc: { color: '#666', fontSize: 15, maxWidth: 480, lineHeight: 1.6, marginBottom: 24 },
  emptyText: { color: '#999', fontSize: 13, padding: 20, textAlign: 'center' },
  prereqNote: { color: '#999', fontSize: 12, marginTop: 16, maxWidth: 420 },

  // Plan picker
  pickerGrid: { display: 'flex', gap: 16, flex: 1, minHeight: 300 },
  pickerColumn: { flex: 1, border: '1px solid #e0e0e0', borderRadius: 8, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  pickerColumnTitle: { margin: 0, padding: '12px 14px', fontSize: 14, fontWeight: 600, borderBottom: '1px solid #e0e0e0', background: '#fafafa' },
  pickerList: { flex: 1, overflow: 'auto', padding: 8 },
  pickerItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13, marginBottom: 2, transition: 'background 0.1s' },
  pickerItemActive: { background: '#e3f2fd' },
  groupIcon: { fontSize: 16 },
  planIcon: { fontSize: 16 },

  // Spinners
  spinner: { width: 36, height: 36, border: '3px solid #e0e0e0', borderTopColor: '#1976d2', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  spinnerSmall: { width: 24, height: 24, border: '2px solid #e0e0e0', borderTopColor: '#1976d2', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
};

// Inject keyframes for spinner
if (typeof document !== 'undefined' && !document.getElementById('planner-spin-keyframes')) {
  const styleEl = document.createElement('style');
  styleEl.id = 'planner-spin-keyframes';
  styleEl.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(styleEl);
}

export default Planner;
